/**
 * Public listing page.
 *
 * Unauthenticated read of a listing by its public slug. Backend
 * `GET /api/v1/listings/public/:slug` is the seam — the response
 * never includes `addressPrivate` and the realtor branding is the
 * minimal display fields (name + agency, no phone / RERA / verified
 * flags).
 *
 * Three branches:
 *   - notFound (404): "This listing isn't available" — same copy
 *     whether unpublished, withdrawn, or never existed (matches the
 *     backend's existence-leak prevention).
 *   - error (network / 5xx): generic retry card.
 *   - loaded: full listing.
 *
 * Sold listings ARE rendered publicly (intentional per Phase 2 design)
 * with a SOLD badge — they remain on the realtor's record. The buyer-
 * handoff flow is a separate, explicit action (Phase 5).
 */

import { ArrowLeft, ImageOff, MapPin, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareListingDialog } from "@/features/realtor/components/share-listing-dialog";
import {
	getPublicListing,
	type PublicListing,
} from "@/features/realtor/services/public-listings-api.service";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";
import { resolveMediaUrl } from "@/lib/utils";

type LoadState =
	| { status: "loading" }
	| { status: "not-found" }
	| { status: "error"; message: string }
	| { status: "loaded"; listing: PublicListing };

export function PublicListingPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { slug } = useParams<{ slug: string }>();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });

	const runLoad = useCallback(
		async (cancelledRef: { cancelled: boolean }) => {
			if (!slug) {
				if (!cancelledRef.cancelled) {
					setLoad({ status: "not-found" });
				}
				return;
			}
			setLoad({ status: "loading" });
			const result = await getPublicListing(slug);
			if (cancelledRef.cancelled) return;
			if (result.notFound) {
				setLoad({ status: "not-found" });
				return;
			}
			if (!result.success || !result.data) {
				setLoad({
					status: "error",
					message:
						result.error ?? t("realtor.publicListing.loadFailedFallback"),
				});
				return;
			}
			setLoad({ status: "loaded", listing: result.data });
		},
		[slug, t],
	);

	useEffect(() => {
		const ref = { cancelled: false };
		runLoad(ref);
		return () => {
			ref.cancelled = true;
		};
	}, [runLoad]);

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen w-full py-12"
				data-testid="public-listing-loading"
			>
				<div className="max-w-5xl mx-auto px-6 space-y-6">
					<Skeleton className="h-72 w-full rounded-xl bg-dashboard-bg" />
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						<div className="lg:col-span-2 space-y-6">
							<Skeleton className="h-32 w-full rounded-xl bg-dashboard-bg" />
							<Skeleton className="h-48 w-full rounded-xl bg-dashboard-bg" />
						</div>
						<div className="lg:col-span-1 space-y-6">
							<Skeleton className="h-48 w-full rounded-xl bg-dashboard-bg" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (load.status === "not-found") {
		return (
			<div className="bg-dashboard-surface min-h-screen w-full flex items-center justify-center p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)] max-w-md w-full">
					<CardContent className="p-8 text-center space-y-3">
						<h1
							className="text-xl font-semibold font-display italic"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.publicListing.notFoundTitle")}
						</h1>
						<p
							className="text-sm font-body max-w-md mx-auto"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.publicListing.notFoundBody")}
						</p>
						<StudioButton variant="primary" onClick={() => navigate("/")}>
							{t("realtor.publicListing.backHome")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen w-full flex items-center justify-center p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)] max-w-md w-full">
					<CardContent className="p-6 text-center space-y-3" role="alert">
						<p
							className="text-sm font-body"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<StudioButton
							variant="secondary"
							onClick={() => runLoad({ cancelled: false })}
						>
							{t("realtor.dashboard.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { listing } = load;
	const isSold = listing.status === "sold";

	return (
		<div className="bg-dashboard-surface min-h-screen w-full pb-12">
			{/* Main Grid */}
			<div className="max-w-5xl mx-auto px-6 pt-8 space-y-8">
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Left Column: Gallery & Details */}
					<div className="lg:col-span-2 space-y-8">
						{/* Photos & Overlay Actions */}
						<div className="relative group">
							<PublicImageGallery images={listing.images} />

							{/* Floating Back Action */}
							<button
								type="button"
								onClick={() => navigate(-1)}
								className="absolute top-4 left-4 inline-flex items-center justify-center rounded-full border shadow-md transition-all duration-300 h-10 w-10 bg-dashboard-bg/80 hover:bg-dashboard-bg backdrop-blur border-dashboard-border text-dashboard-text hover:text-dashboard-accent-dark"
								aria-label={t("realtor.publicListing.back")}
							>
								<ArrowLeft className="h-5 w-5" />
							</button>

							{/* Floating Share Action */}
							<div className="absolute top-4 right-4">
								<ShareListingDialog
									listing={{
										headline: listing.headline,
										locality: listing.locality,
										city: listing.city,
										askingPrice: listing.askingPrice,
										publicSlug: listing.publicSlug,
									}}
									triggerLabel=""
									triggerClassName="rounded-full h-10 w-10 p-0 flex items-center justify-center bg-dashboard-bg/80 hover:bg-dashboard-bg backdrop-blur border-dashboard-border text-dashboard-text hover:text-dashboard-accent-dark"
								/>
							</div>
						</div>

						{/* Headline + Price + Stats Card */}
						<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
							<CardContent className="p-6 sm:p-8 space-y-6">
								<div className="space-y-4">
									<div className="flex items-start justify-between gap-4">
										<h1
											style={{
												fontFamily: "var(--font-display)",
												fontSize: 28,
												fontWeight: 500,
												fontStyle: "italic",
												color: "var(--color-dashboard-text)",
												lineHeight: 1.2,
											}}
										>
											{listing.headline}
										</h1>
										{isSold && (
											<span
												className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shrink-0"
												style={{
													backgroundColor:
														"var(--color-dashboard-pill-done-bg)",
													color: "var(--color-dashboard-pill-done)",
												}}
											>
												{t("realtor.listings.status.sold")}
											</span>
										)}
									</div>
									<p
										style={{
											fontFamily: "var(--font-display)",
											fontSize: 32,
											fontWeight: 500,
											color: "var(--color-dashboard-accent-dark)",
										}}
									>
										{formatPaiseAsRupees(listing.askingPrice)}
									</p>
									<div
										className="flex items-center gap-2 text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										<MapPin className="h-4 w-4 text-dashboard-accent-dark shrink-0" />
										<span className="font-body">
											{listing.addressPublic} · {listing.city}
										</span>
									</div>
								</div>

								<div
									className="flex flex-wrap gap-y-2 gap-x-4 text-xs font-mono border-t pt-4 border-dashboard-border uppercase tracking-wider"
									style={{ color: "var(--color-dashboard-muted)" }}
								>
									<span style={{ color: "var(--color-dashboard-text)" }}>
										{t(`realtor.listings.propertyType.${listing.propertyType}`)}
									</span>
									{listing.bhk != null && <span>· {listing.bhk} BHK</span>}
									{listing.sizeSqft != null && (
										<span>
											· {listing.sizeSqft} {t("realtor.listings.sqft")}
										</span>
									)}
								</div>
							</CardContent>
						</Card>

						{/* Description */}
						{listing.description && (
							<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
								<CardContent className="p-6 sm:p-8 space-y-4">
									<h2
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 11,
											fontWeight: 600,
											letterSpacing: "0.18em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-muted)",
										}}
									>
										{t("realtor.listings.detail.description")}
									</h2>
									<p
										className="text-sm font-body whitespace-pre-line leading-relaxed"
										style={{ color: "var(--color-dashboard-text)" }}
									>
										{listing.description}
									</p>
								</CardContent>
							</Card>
						)}

						{/* Amenities */}
						{listing.amenities.length > 0 && (
							<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
								<CardContent className="p-6 sm:p-8 space-y-4">
									<h2
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 11,
											fontWeight: 600,
											letterSpacing: "0.18em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-muted)",
										}}
									>
										{t("realtor.listings.detail.amenities")}
									</h2>
									<div className="flex flex-wrap gap-2">
										{listing.amenities.map((a) => (
											<span
												key={a}
												className="inline-flex items-center rounded-full px-3 py-1 text-xs border"
												style={{
													backgroundColor: "var(--color-dashboard-surface)",
													borderColor: "var(--color-dashboard-border)",
													color: "var(--color-dashboard-text)",
												}}
											>
												{a}
											</span>
										))}
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					{/* Right Column: Realtor Branding & Disclaimers */}
					<div className="lg:col-span-1 space-y-6">
						{/* Realtor Branding */}
						<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
							<CardContent className="p-6 space-y-4">
								<div className="flex items-center gap-3 border-b pb-3 border-dashboard-border">
									<div
										className="rounded-full p-2.5 shrink-0"
										style={{
											backgroundColor:
												"var(--color-dashboard-pill-awaiting-bg)",
										}}
									>
										<ShieldCheck
											className="h-5 w-5"
											style={{ color: "var(--color-dashboard-pill-awaiting)" }}
										/>
									</div>
									<div>
										<p
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 10,
												fontWeight: 600,
												letterSpacing: "0.15em",
												textTransform: "uppercase",
												color: "var(--color-dashboard-muted)",
											}}
										>
											{t("realtor.publicListing.listedBy")}
										</p>
										<p
											className="text-sm font-semibold"
											style={{ color: "var(--color-dashboard-text)" }}
										>
											{listing.realtor.name}
										</p>
									</div>
								</div>

								{listing.realtor.agencyName && (
									<div className="space-y-1">
										<p className="text-[10px] font-mono uppercase tracking-wider text-dashboard-muted">
											Agency
										</p>
										<p className="text-sm font-medium text-dashboard-text">
											{listing.realtor.agencyName}
										</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Exclusivity disclaimer */}
						{!listing.isExclusive && (
							<div
								className="rounded-lg border p-4 text-center text-xs leading-relaxed"
								style={{
									backgroundColor: "var(--color-dashboard-bg)",
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{t("realtor.publicListing.nonExclusiveDisclaimer")}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

interface GalleryProps {
	images: string[];
}

function PublicImageGallery({ images }: GalleryProps) {
	const { t } = useTranslation();
	const [activeIndex, setActiveIndex] = useState(0);

	if (images.length === 0) {
		return (
			<Card className="aspect-video bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent
					className="h-full flex items-center justify-center gap-2"
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					<ImageOff className="h-5 w-5" />
					<span className="text-sm">{t("realtor.publicListing.noImages")}</span>
				</CardContent>
			</Card>
		);
	}

	const safeActive = Math.min(activeIndex, images.length - 1);
	const active = images[safeActive];

	const handleTabKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "ArrowRight") {
			event.preventDefault();
			setActiveIndex((i) => (i + 1) % images.length);
		} else if (event.key === "ArrowLeft") {
			event.preventDefault();
			setActiveIndex((i) => (i - 1 + images.length) % images.length);
		} else if (event.key === "Home") {
			event.preventDefault();
			setActiveIndex(0);
		} else if (event.key === "End") {
			event.preventDefault();
			setActiveIndex(images.length - 1);
		}
	};

	return (
		<div className="space-y-3">
			<div
				className="aspect-video rounded-xl overflow-hidden border border-dashboard-border"
				style={{ backgroundColor: "var(--color-dashboard-surface)" }}
				role="tabpanel"
				id={`listing-image-panel-${safeActive}`}
				aria-labelledby={`listing-image-tab-${safeActive}`}
			>
				<img
					src={resolveMediaUrl(active)}
					alt={t("realtor.publicListing.imageAlt", {
						index: safeActive + 1,
						total: images.length,
					})}
					className="w-full h-full object-cover"
				/>
			</div>
			{images.length > 1 && (
				<div
					className="flex gap-2 overflow-x-auto"
					role="tablist"
					aria-label={t("realtor.publicListing.galleryTablistLabel")}
				>
					{images.map((url, idx) => (
						<button
							type="button"
							key={url}
							id={`listing-image-tab-${idx}`}
							role="tab"
							aria-selected={idx === safeActive}
							aria-controls={`listing-image-panel-${idx}`}
							tabIndex={idx === safeActive ? 0 : -1}
							onClick={() => setActiveIndex(idx)}
							onKeyDown={handleTabKeyDown}
							className={`shrink-0 h-16 w-20 rounded-xl overflow-hidden border-2 transition ${
								idx === safeActive
									? "border-dashboard-accent"
									: "border-transparent opacity-85 hover:opacity-100"
							}`}
						>
							<img
								src={resolveMediaUrl(url)}
								alt={t("realtor.publicListing.imageThumbAlt", {
									index: idx + 1,
								})}
								className="w-full h-full object-cover"
							/>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
